import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft, Send, User, Check, Shield, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import { v4 as uuidv4 } from "uuid";
import { TrustBadge, ConfirmationNote } from "../components/EmptyState";

const currencySymbols = {
  JMD: "J$",
  USD: "$",
  GBP: "£",
  CAD: "C$"
};

// Debounce hook for search
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
};

const Transfer = ({ user }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [formData, setFormData] = useState({
    recipientEmail: "",
    amount: "",
    currency: "JMD",
    note: ""
  });
  const [transferResult, setTransferResult] = useState(null);

  const debouncedSearch = useDebounce(searchQuery, 300);

  // Search for users by email or username
  const { data: searchResults, isLoading: isSearching } = useQuery({
    queryKey: ["userSearch", debouncedSearch],
    queryFn: async () => {
      if (!debouncedSearch || debouncedSearch.length < 2) return [];
      const response = await axios.get(`${API}/users/search?q=${encodeURIComponent(debouncedSearch)}`);
      return response.data.users || [];
    },
    enabled: debouncedSearch.length >= 2,
    staleTime: 30000,
  });

  const { data: wallets } = useQuery({
    queryKey: ["wallets"],
    queryFn: async () => {
      const response = await axios.get(`${API}/wallets`);
      return response.data;
    }
  });

  const transferMutation = useMutation({
    mutationFn: async (data) => {
      const wallet = wallets?.find(w => w.currency === data.currency);
      if (!wallet) throw new Error("Wallet not found");
      
      const response = await axios.post(`${API}/transfers`, {
        from_wallet_id: wallet.wallet_id,
        to_user_email: data.recipientEmail,
        amount: parseFloat(data.amount),
        currency: data.currency,
        idempotency_key: uuidv4(),
        note: data.note || null
      });
      return response.data;
    },
    onSuccess: (data) => {
      setTransferResult(data);
      setStep(3);
      queryClient.invalidateQueries(["wallets"]);
      queryClient.invalidateQueries(["transactions"]);
      queryClient.invalidateQueries(["recipients"]);
      toast.success("Transfer sent successfully!");
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Transfer failed");
    }
  });

  const selectedWallet = wallets?.find(w => w.currency === formData.currency);

  const handleSelectRecipient = (recipient) => {
    setSelectedRecipient(recipient);
    setFormData({ ...formData, recipientEmail: recipient.email });
    setSearchQuery("");
  };

  const handleContinue = () => {
    if (step === 1) {
      if (!formData.recipientEmail || !formData.recipientEmail.includes("@")) {
        toast.error("Enter a valid email address or select a recipient");
        return;
      }
      setStep(2);
    } else if (step === 2) {
      if (!formData.amount || parseFloat(formData.amount) <= 0) {
        toast.error("Enter a valid amount");
        return;
      }
      if (selectedWallet && parseFloat(formData.amount) > selectedWallet.available_balance) {
        toast.error("Insufficient funds");
        return;
      }
      transferMutation.mutate(formData);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="transfer-page">
      {/* Header */}
      <header className="px-6 py-4 flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => step > 1 ? setStep(step - 1) : navigate("/dashboard")}
          className="text-foreground"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="font-heading font-semibold text-xl">
          {step === 3 ? "Transfer Complete" : "Send Money"}
        </h1>
      </header>

      <main className="px-6 pb-8">
        {/* Progress indicator */}
        {step < 3 && (
          <div className="flex gap-2 mb-8">
            {[1, 2].map(i => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  i <= step ? "bg-primary" : "bg-secondary"
                }`}
              />
            ))}
          </div>
        )}

        {/* Step 1: Recipient */}
        {step === 1 && (
          <div className="animate-slide-up">
            <div className="glass rounded-3xl p-6 mb-6">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <User className="w-8 h-8 text-primary" />
              </div>
              <h2 className="font-heading font-medium text-lg text-center mb-6">
                Who are you sending to?
              </h2>
              
              <div className="space-y-4">
                {/* Single Search/Email Input */}
                <div>
                  <Label htmlFor="search">Email or Username</Label>
                  <div className="relative mt-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="search"
                      data-testid="recipient-search-input"
                      type="text"
                      placeholder="Enter email or @username"
                      className="input-glass pl-10"
                      value={searchQuery || formData.recipientEmail}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSearchQuery(value);
                        setFormData({ ...formData, recipientEmail: value });
                        setSelectedRecipient(null);
                      }}
                    />
                    {isSearching && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
                    )}
                  </div>
                </div>

                {/* Search Results */}
                {searchQuery.length >= 2 && searchResults && searchResults.length > 0 && !selectedRecipient && (
                  <div className="bg-white/5 rounded-xl border border-white/10 max-h-48 overflow-y-auto">
                    {searchResults.map((recipient) => (
                      <button
                        key={recipient.user_id}
                        onClick={() => handleSelectRecipient(recipient)}
                        className="w-full flex items-center gap-3 p-3 hover:bg-white/10 transition-colors text-left border-b border-white/5 last:border-0"
                        data-testid={`search-result-${recipient.user_id}`}
                      >
                        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-semibold text-primary">
                            {recipient.name?.charAt(0)?.toUpperCase() || "?"}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-foreground truncate">{recipient.name}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {recipient.username ? `@${recipient.username} • ` : ""}{recipient.email}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {/* No results message */}
                {searchQuery.length >= 2 && !isSearching && searchResults?.length === 0 && !selectedRecipient && (
                  <p className="text-sm text-muted-foreground text-center py-2">
                    No users found. You can still send to this email if it's valid.
                  </p>
                )}

                {/* Selected Recipient Display */}
                {selectedRecipient && (
                  <div className="bg-primary/10 rounded-xl p-3 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                      <span className="text-sm font-semibold text-primary">
                        {selectedRecipient.name?.charAt(0)?.toUpperCase() || "?"}
                      </span>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm text-foreground">{selectedRecipient.name}</p>
                      <p className="text-xs text-muted-foreground">{selectedRecipient.email}</p>
                    </div>
                    <Check className="w-5 h-5 text-primary" />
                  </div>
                )}
              </div>
            </div>
            
            <Button
              data-testid="continue-btn"
              className="w-full btn-primary h-14 text-base"
              onClick={handleContinue}
              disabled={!formData.recipientEmail}
            >
              Continue
            </Button>
          </div>
        )}

        {/* Step 2: Amount */}
        {step === 2 && (
          <div className="animate-slide-up">
            <div className="glass rounded-3xl p-6 mb-6">
              <p className="text-xs uppercase tracking-widest text-muted-foreground/70 text-center mb-2">
                Sending to
              </p>
              <p className="text-center text-foreground font-medium mb-6">
                {formData.recipientEmail}
              </p>
              
              <div className="space-y-4">
                <div>
                  <Label>Currency</Label>
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    {["JMD", "USD", "GBP", "CAD"].map(c => (
                      <button
                        key={c}
                        data-testid={`currency-${c}`}
                        onClick={() => setFormData({ ...formData, currency: c })}
                        className={`p-3 rounded-xl text-sm font-medium transition-all ${
                          formData.currency === c
                            ? "bg-primary text-primary-foreground"
                            : "bg-white/5 text-muted-foreground hover:bg-white/10"
                        }`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between items-center">
                    <Label htmlFor="amount">Amount</Label>
                    {selectedWallet && (
                      <span className="text-xs text-muted-foreground">
                        Available: {currencySymbols[formData.currency]}{selectedWallet.available_balance.toLocaleString()}
                      </span>
                    )}
                  </div>
                  <div className="relative mt-2">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">
                      {currencySymbols[formData.currency]}
                    </span>
                    <Input
                      id="amount"
                      data-testid="amount-input"
                      type="number"
                      placeholder="0.00"
                      className="input-glass pl-10 text-2xl font-mono-numbers h-16"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="note">Note (optional)</Label>
                  <Input
                    id="note"
                    data-testid="note-input"
                    type="text"
                    placeholder="What's this for?"
                    className="input-glass mt-2"
                    value={formData.note}
                    onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  />
                </div>
              </div>
            </div>
            
            {/* Fee breakdown */}
            <div className="glass rounded-2xl p-4 mb-4">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-muted-foreground">Amount</span>
                <span className="font-mono-numbers">
                  {currencySymbols[formData.currency]}{parseFloat(formData.amount || 0).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-muted-foreground">Fee (1%)</span>
                <span className="font-mono-numbers">
                  {currencySymbols[formData.currency]}{(parseFloat(formData.amount || 0) * 0.01).toFixed(2)}
                </span>
              </div>
              <div className="border-t border-white/10 pt-2 flex justify-between">
                <span className="font-medium">Total</span>
                <span className="font-mono-numbers font-bold text-primary">
                  {currencySymbols[formData.currency]}{(parseFloat(formData.amount || 0) * 1.01).toFixed(2)}
                </span>
              </div>
            </div>
            
            <TrustBadge
              icon={Shield}
              text="You'll review this before confirming. Transfers can't be reversed once sent."
              variant="default"
            />
            
            <Button
              data-testid="send-btn"
              className="w-full btn-primary h-14 text-base mt-4"
              onClick={handleContinue}
              disabled={transferMutation.isPending}
            >
              {transferMutation.isPending ? (
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                  Sending...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Send className="w-5 h-5" />
                  Send Money
                </span>
              )}
            </Button>
          </div>
        )}

        {/* Step 3: Success */}
        {step === 3 && transferResult && (
          <div className="animate-slide-up text-center">
            <div className="glass rounded-3xl p-8 mb-6">
              <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6 animate-pulse-glow">
                <Check className="w-10 h-10 text-primary" />
              </div>
              
              <h2 className="font-heading font-bold text-2xl mb-2">Money Sent!</h2>
              <p className="text-muted-foreground mb-6">
                Your transfer has been completed successfully
              </p>
              
              <div className="space-y-3">
                <div className="flex justify-between py-3 border-b border-white/10">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="font-mono-numbers font-medium">
                    {currencySymbols[transferResult.currency]}{transferResult.amount.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between py-3 border-b border-white/10">
                  <span className="text-muted-foreground">Fee</span>
                  <span className="font-mono-numbers">
                    {currencySymbols[transferResult.currency]}{transferResult.fee.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between py-3 border-b border-white/10">
                  <span className="text-muted-foreground">To</span>
                  <span>{formData.recipientEmail}</span>
                </div>
                <div className="flex justify-between py-3">
                  <span className="text-muted-foreground">Status</span>
                  <span className="text-primary font-medium">{transferResult.status}</span>
                </div>
              </div>
            </div>
            
            <Button
              data-testid="done-btn"
              className="w-full btn-primary h-14 text-base"
              onClick={() => navigate("/dashboard")}
            >
              Done
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

export default Transfer;
